import moment from 'moment';
import BaseAccountController from '../../lib/controllers/BaseAccountController';
import queryParameterParser from '../../lib/helpers/queryStringParser';

declare var LtFramework: lt.Static;

export default class CreateAccountController extends BaseAccountController {
	public static $inject: string[] = ['$scope', '$window', 'navigationService', 'UserAccountResource'];

	public account: MyAccountApi.IUserAccount;
	public accountCreated = false;
	public birthDate: string; // MM/dd/YYYY
	public generalError: string;
	public generalMessage: string;
	public invalidSubmission = false;
	public allowNonMember: boolean;
	public placeholderText: string;

	private readonly $window: ng.IWindowService;
	private readonly navigationService: INavigationService;
	private readonly UserAccount: MyAccountApi.IUserAccountResource;

	constructor($scope: ng.IScope, $window: ng.IWindowService, navigationService: INavigationService,
			UserAccountResource: MyAccountApi.IUserAccountResource) {
		super($scope);

		this.$window = $window;
		this.navigationService = navigationService;
		this.UserAccount = UserAccountResource;

		this.account = new this.UserAccount();
	}

	public validateConfirmEmailAddress(ctrl: ng.INgModelController): void {
		ctrl.$setValidity('matchesEmailAddress', this.account.emailAddress === this.account.emailAddressConfirm);
	}

	public validateConfirmPassword(ctrl: ng.INgModelController): void {
		ctrl.$setValidity('matchesPassword', this.account.password === this.account.passwordConfirm);
	}

	public createAccount(form: ng.IFormController): void {
		this.generalError = '';
		this.generalMessage = '';

		if (form.$invalid) {
			this.setError('Unable to create account due to invalid form fields. Please correct them and try again.');
			return;
		}

		const dob = moment(this.birthDate, 'MM-DD-YYYY');

		// Users younger than 13 aren't legally allowed to create accounts
		const age = moment().diff(dob, 'years');
		if (age < 13) {
			this.invalidSubmission = true;
			return;
		}

		// convert birth date into format that the service expects
		this.account.dob = {
			dateStr: dob.format('YYYY-MM-DD')
		};

		// Save email address, since calling $create will replace this.account with the return value
		// of the create request which is NOT an account object (contains just a single autoActivated field).
		// The page needs to show the email address after a successful save.
		const emailAddress = this.account.emailAddress;

		this.account.$create((resp: MyAccountApi.IUserAccountCreatedResponse) => {
			// restore emailAddress
			this.account.emailAddress = emailAddress;

			// if the account was auto-activated, no need to show them the activation email sent text.
			// We instead simply redirect them to the login page to start using their account.
			if (resp.autoActivated) {
				this.navigationService.requestNavigation('accountLogin').catch((err: Error) => {
					// tslint:disable-next-line: no-console
					console.log(err);
				});
				return;
			}

			// show the activation email sent text
			this.accountCreated = true;
		}, (failure: MyAccountApi.IServiceFailureResponse) => {
			if (failure && failure.data && failure.data.validationErrors) {
				if (failure.data.validationErrors[0].message.indexOf('already have an account') >= 0) {
					const forgotUsernameUrl = this.navigationService.resolveDestination('account.forgotUsername');

					this.setError('An account is already associated with this Member number. '
						+ ' If you’ve forgotten your username, you can request a reminder <a href="' + forgotUsernameUrl + '">here</a>.');
				} else {
					this.setError(failure.data.validationErrors[0].message);
				}
			} else if (failure.data.errorMessage) {
				if (failure.data.errorMessage.indexOf('User is too young') >= 0 ) {
					this.setError('User is too young for online account');
				} else {
					this.setError(failure.data.errorMessage);
				}
			} else {
				this.setError('Server error while creating account');
			}
		});
	}

	public cancel(): void {
		this.$window.history.go(-1);
	}

	public resendActivationEmail(): void {
		alert('Resend Activation Email story not done');
	}

	public changePlaceholder(): string {
		const allowNonMemberParam = queryParameterParser.getParameterByName(this.$window, 'allowNonMember');

		if (allowNonMemberParam != null && allowNonMemberParam === 'true') {
			this.allowNonMember = true;
			return 'Member Number (optional)';
		} else {
			this.allowNonMember = false;
			return 'Member Number';
		}
	}

	// shows a general error message, clears any other general messages, and scrolls to the top of the page
	private setError(errorMessage: string): void {
		this.generalError = errorMessage;
		this.generalMessage = '';
		this.$window.scrollTo(0, 0);
	}
}
